// Renders streamed hate-speech response with same colors/layout as FriendlyAnalysis.
// visibleText = content revealed so far; result = full API result for refs/links; sectionRanges = [{ start, end, type }].
import React from 'react';
import { useTranslation } from 'react-i18next';

function StreamingHateSpeechResponse({ visibleText, result, sectionRanges, isStreaming }) {
  const { t } = useTranslation();
  if (!visibleText && !result) return null;

  const visibleLength = (visibleText || '').length;
  const parsed = result && typeof result === 'object' ? result : null;

  // Build segments: for each section range, if we have visible content in that range, render it with the right style
  const segments = [];
  if (sectionRanges && sectionRanges.length > 0) {
    for (const range of sectionRanges) {
      const start = range.start;
      const end = Math.min(range.end, visibleLength);
      if (end <= start) continue;
      const text = (visibleText || '').substring(start, end);
      if (!text.trim() && range.type !== 'intro') continue;
      segments.push({ type: range.type, text, isPartial: visibleLength < range.end });
    }
  } else {
    // Fallback: show visible text as single block
    segments.push({ type: 'intro', text: visibleText || '', isPartial: isStreaming });
  }

  const refs = parsed?.references && Array.isArray(parsed.references) ? parsed.references : [];
  const categories = Array.isArray(parsed?.categories) ? parsed.categories : [];
  const keywords = Array.isArray(parsed?.keywords) ? parsed.keywords : [];
  const patterns = Array.isArray(parsed?.detected_patterns) ? parsed.detected_patterns : [];

  const getCategoryLabel = (raw) => {
    const fallback = raw ? String(raw).replace(/_/g, ' ') : '';
    return t(`report.categories.${raw}`, { defaultValue: fallback });
  };
  const getSeverityClass = (severity) => {
    const s = (severity || '').toLowerCase();
    if (s === 'high' || s === 'critical') return 'text-red-600 dark:text-red-400';
    if (s === 'medium') return 'text-orange-600 dark:text-orange-400';
    return 'text-green-600 dark:text-green-400';
  };

  return (
    <div className="space-y-3 text-sm text-shayek-light-900 dark:text-shayek-dark-800">
      {segments.map((seg, idx) => {
        if (seg.type === 'intro' || seg.type === 'why') {
          return (
            <div key={idx} className="whitespace-pre-line leading-relaxed">
              {seg.text}
              {seg.isPartial && isStreaming && (
                <span className="inline-block w-2 h-4 ml-0.5 bg-shayek-primary dark:bg-shayek-red-light animate-pulse align-middle" aria-hidden="true" />
              )}
            </div>
          );
        }
        if (seg.type === 'references') {
          const lines = seg.text.split('\n').filter((l) => l.length > 0);
          const hasHeader = lines[0] && (lines[0].includes('Based on') || lines[0].includes('References'));
          const refLines = hasHeader ? lines.slice(1).filter((l) => l.trim()) : lines.filter((l) => l.trim());
          const header = hasHeader ? lines[0] : null;
          return (
            <div
              key={idx}
              className="mt-3 p-3 bg-shayek-primary/5 dark:bg-shayek-dark-100 rounded-lg border border-shayek-primary/20 dark:border-shayek-dark-300"
            >
              {header && (
                <h4 className="text-xs font-semibold text-shayek-light-800 dark:text-shayek-dark-700 uppercase tracking-wider mb-2">
                  {header}
                </h4>
              )}
              <ul className="space-y-1">
                {refLines.map((line, i) => {
                  const ref = refs.find((r) => (r.source || r.title || r.url) === line || (r.source && line.startsWith(r.source)));
                  return (
                    <li key={i}>
                      {ref?.url ? (
                        <a
                          href={ref.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-shayek-primary dark:text-red-300 hover:underline text-sm"
                        >
                          {line}
                        </a>
                      ) : (
                        <span className="text-shayek-light-700 dark:text-shayek-dark-600 text-sm">{line}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
              {seg.isPartial && isStreaming && (
                <span className="inline-block w-2 h-4 ml-0.5 bg-shayek-primary dark:bg-shayek-red-light animate-pulse align-middle" aria-hidden="true" />
              )}
            </div>
          );
        }
        if (seg.type === 'confidence') {
          return (
            <div key={idx}>
              <span className="text-shayek-light-900 dark:text-shayek-dark-900 font-medium">{seg.text}</span>
              {seg.isPartial && isStreaming && (
                <span className="inline-block w-2 h-4 ml-0.5 bg-shayek-primary dark:bg-shayek-red-light animate-pulse align-middle" aria-hidden="true" />
              )}
            </div>
          );
        }
        if (seg.type === 'severity') {
          const lines = seg.text.split('\n').filter((l) => l.length > 0);
          const labelLine = lines[0] || '';
          const valueLine = lines[1] || lines[0] || '';
          const value = labelLine.toLowerCase().includes('severity') ? valueLine : (lines[0] || '');
          return (
            <div key={idx}>
              <span className="text-xs text-shayek-light-500 dark:text-shayek-dark-600 block">Severity Level</span>
              <span className={`text-sm font-medium capitalize ${getSeverityClass(value)}`}>{value.trim() || '—'}</span>
              {seg.isPartial && isStreaming && (
                <span className="inline-block w-2 h-4 ml-0.5 bg-shayek-primary dark:bg-shayek-red-light animate-pulse align-middle" aria-hidden="true" />
              )}
            </div>
          );
        }
        if (seg.type === 'categories') {
          const lines = seg.text.split('\n').filter((l) => l.trim());
          const values = lines[0]?.includes('Detected') ? lines.slice(1) : lines;
          const toShow = values.length > 0 ? values : categories.map(getCategoryLabel);
          return (
            <div key={idx}>
              <span className="text-xs text-shayek-light-500 dark:text-shayek-dark-600 block mb-1">
                {t('journalist.summarize.detectedCategories', 'Detected Categories')}
              </span>
              <div className="flex flex-wrap gap-1">
                {(toShow.length ? toShow : ['—']).map((label, i) => (
                  <span
                    key={i}
                    className="px-2 py-0.5 bg-shayek-primary/10 text-shayek-primary dark:bg-orange-900/40 dark:text-orange-200 rounded text-xs font-medium border border-shayek-primary/20 dark:border-orange-500/60"
                  >
                    {label}
                  </span>
                ))}
              </div>
              {seg.isPartial && isStreaming && (
                <span className="inline-block w-2 h-4 ml-0.5 bg-shayek-primary dark:bg-shayek-red-light animate-pulse align-middle" aria-hidden="true" />
              )}
            </div>
          );
        }
        if (seg.type === 'keywords') {
          const lines = seg.text.split('\n').filter((l) => l.trim());
          const values = lines[0]?.toLowerCase().includes('trigger') ? lines.slice(1) : lines;
          const toShow = values.length > 0 ? values : keywords;
          if (toShow.length === 0 && keywords.length === 0) return null;
          return (
            <div key={idx}>
              <span className="text-xs text-shayek-light-500 dark:text-shayek-dark-600 block mb-1">Trigger Words/Flags</span>
              <div className="flex flex-wrap gap-1">
                {toShow.map((word, i) => (
                  <span
                    key={i}
                    className="px-2 py-0.5 bg-red-50 text-red-700 dark:bg-red-900/40 dark:text-red-200 rounded text-xs font-medium border border-red-100 dark:border-red-700"
                  >
                    {word}
                  </span>
                ))}
              </div>
              {seg.isPartial && isStreaming && (
                <span className="inline-block w-2 h-4 ml-0.5 bg-shayek-primary dark:bg-shayek-red-light animate-pulse align-middle" aria-hidden="true" />
              )}
            </div>
          );
        }
        if (seg.type === 'patterns') {
          const lines = seg.text.split('\n').filter((l) => l.trim());
          const values = lines[0]?.toLowerCase().includes('pattern') ? lines.slice(1) : lines;
          const toShow = values.length > 0 ? values : patterns;
          return (
            <div key={idx}>
              <span className="text-xs text-shayek-light-500 dark:text-shayek-dark-600 block mb-1">
                {t('common.detectedPatterns', 'Detected Patterns')}
              </span>
              <div className="flex flex-wrap gap-1">
                {(toShow.length ? toShow : ['—']).map((p, i) => (
                  <span
                    key={i}
                    className="px-2 py-0.5 bg-purple-50 text-purple-700 dark:bg-purple-900/40 dark:text-purple-200 rounded text-xs font-medium border border-purple-200 dark:border-purple-700"
                  >
                    {p}
                  </span>
                ))}
              </div>
              {seg.isPartial && isStreaming && (
                <span className="inline-block w-2 h-4 ml-0.5 bg-shayek-primary dark:bg-shayek-red-light animate-pulse align-middle" aria-hidden="true" />
              )}
            </div>
          );
        }
        return (
          <div key={idx} className="whitespace-pre-line">
            {seg.text}
            {seg.isPartial && isStreaming && (
              <span className="inline-block w-2 h-4 ml-0.5 bg-shayek-primary dark:bg-shayek-red-light animate-pulse align-middle" aria-hidden="true" />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default StreamingHateSpeechResponse;
